/**
 * Projects Data Query Keys
 *
 * Query key factory for React Query cache management.
 * Following the recommended pattern from TanStack Query docs.
 *
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/query-keys
 */

import { isTestnet } from 'web3';

/**
 * Get current network identifier for query key scoping
 */
const getNetworkScope = (): 'mainnet' | 'testnet' => (isTestnet ? 'testnet' : 'mainnet');

/**
 * Query keys for projects data
 *
 * Usage:
 * - `PROJECTS_DATA_QUERY_KEYS.all` - matches all projects queries (for mass invalidation)
 * - `PROJECTS_DATA_QUERY_KEYS.list()` - specific list query for current network
 */
export const PROJECTS_DATA_QUERY_KEYS = {
  /**
   * Base key for all projects data queries
   * Use for invalidating all projects-related queries
   */
  all: ['projects-data'] as const,

  /**
   * Key for the main projects list query
   * Scoped by network (mainnet/testnet)
   */
  list: () => [...PROJECTS_DATA_QUERY_KEYS.all, 'list', getNetworkScope()] as const,

  /**
   * Key for a specific project (future use)
   */
  detail: (projectId: number) =>
    [...PROJECTS_DATA_QUERY_KEYS.all, 'detail', getNetworkScope(), projectId] as const,
} as const;

/**
 * Type for the list query key
 */
export type ProjectsListQueryKey = ReturnType<typeof PROJECTS_DATA_QUERY_KEYS.list>;
